import { useGetBreeds, useLog } from '@application/api';
import { useLitterFormState } from '@application/state';
import { scrollToError, useModal, useRouter, useWindowConfirmUnload } from '@application/utils';
import { AnimalStatCreatedFromEnum, ParentTypeEnum } from '@gql/graphql';
import { ConfirmationModal } from '@presentation/components';
import type { MtAnimalsBreederPageFormValues } from '@presentation/pages';
import { MtAnimalBreederPage } from '@presentation/pages';
import type { FormMode } from '@presentation/types';
import { FormikProvider, useFormik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import { useAnimalInitialValuesBuilder } from './api/useAnimalIntialValuesBuilder';
import { useArchiveAnimal } from './api/useArchiveAnimal';
import { useCreateAnimal } from './api/useCreateAnimal';
import { useGetAnimal } from './api/useGetAnimal';
import { useUnarchiveAnimal } from './api/useUnarchiveAnimal';
import { useUpdateAnimal } from './api/useUpdateAnimal';
import { useValidateAnimalForm } from './useValidateAnimalForm';

export const MtAnimalBreederPageController = () => {
  const { infoFromCurrentPath, translate, navigateToRoute } = useRouter();
  const { modal, show, close } = useModal();
  const { modal: modalUnarchive, show: showUnarchive, close: closeUnarchive } = useModal();
  const { log } = useLog();
  const id = useMemo(() => {
    const idRaw = infoFromCurrentPath.params[0];
    if (!idRaw || idRaw === 'new') return;
    return Number(idRaw);
  }, [infoFromCurrentPath.params]);
  const [modeState, setMode] = useState<FormMode>('view');

  const { validateAnimalForm } = useValidateAnimalForm();
  const { animal, animalLoading } = useGetAnimal(id);
  const { breedOptionsWithoutCrossbreeds, breedsLoading } = useGetBreeds();
  const { createAnimalFromForm } = useCreateAnimal();
  const { animalInitialValuesBuilder } = useAnimalInitialValuesBuilder();
  const { archiveAnimal } = useArchiveAnimal();
  const { unarchiveAnimal } = useUnarchiveAnimal();

  const { setLitterFormState, litterFormState } = useLitterFormState();
  const { parentType } = infoFromCurrentPath.search;

  const mode = useMemo((): FormMode => {
    if (!id || !animal) return 'new';
    if (animal.archived) return 'view';
    return modeState;
  }, [id, animal, modeState]);

  const { updateAnimalFromForm } = useUpdateAnimal();

  const backClickHandler = useCallback(() => {
    if (infoFromCurrentPath.search.parentType) {
      return navigateToRoute('mtLitters', {
        params: [litterFormState.litterId ? String(litterFormState.litterId) : 'new'],
      });
    }
    navigateToRoute('mtAnimals');
  }, [infoFromCurrentPath.search.parentType, litterFormState.litterId, navigateToRoute]);

  const formSubmitHandler = useCallback(
    async (values: MtAnimalsBreederPageFormValues) => {
      if (mode === 'new') {
        const createdAnimal = await createAnimalFromForm({
          values,
          createdFrom: AnimalStatCreatedFromEnum.MANUAL_BREEDER,
        });
        const createdAnimalId = createdAnimal.data?.createAnimal?.id;
        const createdAnimalPedigree = createdAnimal.data?.createAnimal?.dogInfos?.pedigree;
        if (createdAnimalId && parentType && litterFormState) {
          setLitterFormState({
            values: {
              ...litterFormState.values,
              [parentType as ParentTypeEnum]: createdAnimalId,
              ...(parentType === ParentTypeEnum.FATHER
                ? { fatherPedigree: createdAnimalPedigree }
                : { motherPedigree: createdAnimalPedigree }),
            },
            litterId: litterFormState.litterId ?? 'new',
            mode: litterFormState.litterId !== 'new' ? 'edit' : 'new',
          });
        }
      } else {
        await updateAnimalFromForm(values, animal);
      }

      backClickHandler();
    },
    [
      animal,
      backClickHandler,
      createAnimalFromForm,
      litterFormState,
      mode,
      parentType,
      setLitterFormState,
      updateAnimalFromForm,
    ],
  );

  const deleteConfirmHandler = useCallback(async () => {
    close();
    await archiveAnimal(animal?.id);
    navigateToRoute('mtAnimals');
  }, [close, archiveAnimal, animal?.id, navigateToRoute]);

  const unarchiveConfirmHandler = useCallback(async () => {
    close();
    await unarchiveAnimal(animal?.id);
    navigateToRoute('mtAnimals');
  }, [animal?.id, close, navigateToRoute, unarchiveAnimal]);

  const form = useFormik<MtAnimalsBreederPageFormValues>({
    onSubmit: formSubmitHandler,
    validate: validateAnimalForm,
    initialValues: animalInitialValuesBuilder(mode !== 'new' ? animal : undefined),
    enableReinitialize: true,
  });

  const onSubmitClickHandler = useCallback(async () => {
    await form.submitForm();
    scrollToError(form.errors);
  }, [form]);

  useWindowConfirmUnload({ formIsDirty: form?.dirty, formIsSubmitting: form?.isSubmitting });

  return (
    <FormikProvider value={form}>
      <ConfirmationModal
        modal={modal}
        onConfirm={deleteConfirmHandler}
        title={translate('common.modals.archiveParent.title')}
        text={translate('common.modals.archiveParent.text')}
      />
      <ConfirmationModal
        modal={modalUnarchive}
        onConfirm={unarchiveConfirmHandler}
        title={translate('common.modals.unarchiveParent.title')}
        text={translate('common.modals.unarchiveParent.text')}
      />
      <MtAnimalBreederPage
        onSubmitClick={onSubmitClickHandler}
        loading={breedsLoading || animalLoading}
        disabledParentType={!!parentType}
        archived={animal?.archived}
        onBackClick={backClickHandler}
        mode={mode}
        onEditClick={() => {
          setMode('edit');
        }}
        onUnarchiveClick={() => showUnarchive()}
        onDeleteClick={() => show()}
        onCancelClick={() => {
          if (mode === 'new') {
            backClickHandler();
          } else {
            setMode('view');
          }
        }}
        options={{
          breeds: breedOptionsWithoutCrossbreeds,
        }}
        log={log}
      />
    </FormikProvider>
  );
};
